import { Sparkles, PhoneIncoming, PhoneOutgoing } from 'lucide-react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useContact } from '../../api/contacts'
import { useCallPurposes } from '../../api/callPurposes'
import { OutcomeBadge } from '../shared/OutcomeBadge'
import { SpinLoader } from '../shared/SpinLoader'
import { timeAgo } from '../../lib/utils'
import type { ContactRecentCall } from '../../api/contacts'

function lastTouchByPurpose(recentCalls: ContactRecentCall[]): {
  outbound: ContactRecentCall[]
  inbound: ContactRecentCall[]
} {
  // recent_calls is newest-first — first occurrence per purpose wins.
  const outbound = new Map<string, ContactRecentCall>()
  const inbound = new Map<string, ContactRecentCall>()
  for (const call of recentCalls) {
    if (!call.purpose) continue
    const bucket = call.direction === 'inbound' ? inbound : outbound
    if (!bucket.has(call.purpose)) bucket.set(call.purpose, call)
  }
  return { outbound: [...outbound.values()], inbound: [...inbound.values()] }
}

interface BriefingProps {
  contactId: number
}

export function CallStagingBriefing({ contactId }: BriefingProps) {
  const { t } = useTranslation('contacts')
  const { data, isLoading, isError } = useContact(contactId)
  const { data: purposeData } = useCallPurposes()

  const purposeLabels: Record<string, string> = {}
  for (const p of purposeData?.outbound ?? []) purposeLabels[p.purpose] = p.label
  for (const r of purposeData?.inbound_reasons ?? []) purposeLabels[r.reason] = r.label

  if (isLoading) return <SpinLoader label={t('briefing.loading')} />
  if (isError || !data) return null

  const recentCalls = data.recent_calls ?? []
  const grouped = lastTouchByPurpose(recentCalls)
  const hasAnything = grouped.outbound.length > 0 || grouped.inbound.length > 0

  if (!hasAnything) {
    return (
      <div className="rounded-lg border border-border bg-surface-card p-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-text-muted" />
          <h2 className="text-lg font-semibold text-text-primary">{t('briefing.title')}</h2>
        </div>
        <p className="mt-3 text-sm text-text-muted">{t('briefing.empty')}</p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-surface-card p-4">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        <h2 className="text-lg font-semibold text-text-primary">{t('briefing.title')}</h2>
      </div>

      <LastTouchByPurposeSection
        outbound={grouped.outbound}
        inbound={grouped.inbound}
        purposeLabels={purposeLabels}
      />
    </div>
  )
}

function LastTouchByPurposeSection({
  outbound,
  inbound,
  purposeLabels,
}: {
  outbound: ContactRecentCall[]
  inbound: ContactRecentCall[]
  purposeLabels: Record<string, string>
}) {
  const { t } = useTranslation('contacts')
  return (
    <div className="mt-4 space-y-3">
      <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
        {t('briefing.lastTouch.title')}
      </p>
      {outbound.length > 0 && (
        <PurposeGroup
          icon={<PhoneOutgoing className="h-3.5 w-3.5 text-text-muted" />}
          label={t('briefing.lastTouch.outbound')}
          calls={outbound}
          purposeLabels={purposeLabels}
        />
      )}
      {inbound.length > 0 && (
        <PurposeGroup
          icon={<PhoneIncoming className="h-3.5 w-3.5 text-text-muted" />}
          label={t('briefing.lastTouch.inbound')}
          calls={inbound}
          purposeLabels={purposeLabels}
        />
      )}
    </div>
  )
}

function PurposeGroup({
  icon,
  label,
  calls,
  purposeLabels,
}: {
  icon: React.ReactNode
  label: string
  calls: ContactRecentCall[]
  purposeLabels: Record<string, string>
}) {
  const { t } = useTranslation('contacts')
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
        {icon}
        {label}
      </div>
      <div className="space-y-1.5">
        {calls.map((call) => (
          <Link
            key={call.call_sid}
            to={`/calls/${call.call_sid}`}
            className="group flex items-start gap-2.5 rounded-md border border-border/60 bg-surface px-3 py-2 text-sm transition-colors hover:bg-surface-elevated"
          >
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-text-primary">
                {purposeLabels[call.purpose ?? ''] ?? call.purpose}
              </span>
              <OutcomeBadge outcome={call.outcome ?? null} size="sm" />
            </div>
            <p className="line-clamp-2 flex-1 text-xs text-text-secondary">
              {call.call_summary || (
                <span className="italic text-text-muted">{t('briefing.lastTouch.noSummary')}</span>
              )}
            </p>
            <span className="whitespace-nowrap text-[11px] text-text-muted">
              {timeAgo(call.started_at)}
            </span>
          </Link>
        ))}
      </div>
    </div>
  )
}
